import * as turf from '@turf/simplify';
import { isFunction } from 'lodash';
import { Subject } from 'rxjs';
import { takeUntil, take } from 'rxjs/operators';

import { coerceNumberProperty } from '@angular/cdk/coercion';
import { CdkPortalOutlet } from '@angular/cdk/portal';
import {
  Input,
  Output,
  Inject,
  NgZone,
  Injector,
  Optional,
  Component,
  OnDestroy,
  ViewChild,
  ElementRef,
  EventEmitter,
  AfterViewInit,
  ViewContainerRef,
  ViewEncapsulation,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';

import { GoogleDrawingManager } from '@lib/geo/google/drawing';
import {
  SnLonLatLike,
  SnLonLat,
  SnLonLatBounds,
  FsMapCircle,
  FsMapPolygon,
  FsMapPolyline,
  FsMapRectangle,
  FsMapControlsModes,
} from '@lib/geo/helpers';
import { BASE_COLORS, COLORS } from '@lib/ui/theme';

import { GoogleMapStyle } from '../../enums';
import { GoogleMap, IGoogleMapOptions } from '../../map';
import { DEFAULT_MAP_OPTIONS } from '../../providers';
import { GoogleTileMonitorService } from '../../services/google-tile-monitor.service';
import { MapDefaultOptions } from '../../types';

@Component({
  selector: 'sn-google-map',
  host: {
    class: 'sn-map sn-google-map',
  },
  templateUrl: './google-map.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GoogleMapComponent implements AfterViewInit, OnDestroy {
  @Input()
  public options: IGoogleMapOptions = {};

  @Input()
  set center(value: SnLonLatLike) {
    if (this._mapInstance) {
      this._mapInstance.setCenter(value);
    } else if (this.options) {
      this.options.center = value;
    }
  }

  @Input()
  set zoom(value: number) {
    const zoom = coerceNumberProperty(value);

    if (this._mapInstance) {
      this._mapInstance.setZoom(zoom);
    } else if (this.options) {
      this.options.zoom = zoom;
    }
  }

  @Input()
  set tilt(value: number) {
    const tilt = coerceNumberProperty(value);

    if (this._mapInstance) {
      this._mapInstance.setTilt(tilt);
    } else if (this.options) {
      this.options.tilt = tilt;
    }
  }

  @Output()
  public readonly mapInit = new EventEmitter<GoogleMap>();

  @Output()
  public readonly mapClick = new EventEmitter<SnLonLat>();

  @Output()
  public readonly mapDblclick = new EventEmitter<SnLonLat>();

  @Output()
  public readonly mapDragStart = new EventEmitter<void>();

  @Output()
  public readonly mapDrag = new EventEmitter<void>();

  @Output()
  public readonly mapDragEnd = new EventEmitter<void>();

  @Output()
  public readonly centerChange = new EventEmitter<SnLonLat>();

  @Output()
  public readonly boundsChange = new EventEmitter<SnLonLatBounds>();

  @Output()
  public readonly zoomChange = new EventEmitter<number>();

  @Output()
  public readonly styleChange = new EventEmitter<GoogleMapStyle>();

  @ViewChild(CdkPortalOutlet, { static: true })
  private readonly _portalOutlet: CdkPortalOutlet;

  private _mapInstance: GoogleMap;
  private _drawingManager: GoogleDrawingManager;
  private _drawingMode: FsMapControlsModes;

  private readonly _defaultOptions: IGoogleMapOptions;
  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _ngZone: NgZone,
    private readonly _changeDetectorRef: ChangeDetectorRef,
    private readonly _elementRef: ElementRef<HTMLElement>,
    private readonly _injector: Injector,
    private readonly _viewContainerRef: ViewContainerRef,
    private readonly _tileMonitorService: GoogleTileMonitorService,
    @Inject(DEFAULT_MAP_OPTIONS) @Optional() defaultOptions: MapDefaultOptions,
  ) {
    this._defaultOptions = isFunction(defaultOptions) ? defaultOptions() : defaultOptions;
    this._changeDetectorRef.detach();

    // Initialize tile monitor service
    this._tileMonitorService.initialize();
  }

  public onMapInit(map: any): void {
    this.mapInit.emit(map);
    this.mapInit.complete();
  }

  public ngAfterViewInit(): void {
    this._ngZone.runOutsideAngular(() => {
      const mapOptions = { ...this._defaultOptions, ...this.options };
      if (mapOptions.tilt) {
        mapOptions.style = GoogleMapStyle.Dark3D;
      }
      this._mapInstance = new GoogleMap(
        this._elementRef.nativeElement.querySelector('.sn-map__container'),
        mapOptions,
        this._portalOutlet,
        this._ngZone,
        this._injector,
        this._viewContainerRef,
      );

      this._mapInstance
        .on('idle')
        .pipe(take(1), takeUntil(this._destroy$))
        .subscribe(() => {
          this.mapInit.emit(this._mapInstance);
          this.mapInit.complete();
        });

      this._watchMap();

      this._initDrawingManager();

      this._mapInstance.startDrawing$.pipe(takeUntil(this._destroy$)).subscribe((res) => {
        if (res.tool === 'pen') {
          this._initPenMode();
          return;
        }
        this._drawingMode = res.mode;
        this._drawingManager.setOptions(res.style);
        this._drawingManager.setDrawingMode(res.tool);
      });
    });
  }

  public ngOnDestroy(): void {
    if (this._mapInstance) {
      this._mapInstance.destroy();
    }
    this._destroy$.next();
    this._destroy$.complete();
  }

  private _watchMap(): void {
    this._mapInstance
      .on('click')
      .pipe(takeUntil(this._destroy$))
      .subscribe(this.mapClick.emit.bind(this.mapClick));

    this._mapInstance
      .on('dblclick')
      .pipe(takeUntil(this._destroy$))
      .subscribe(this.mapDblclick.emit.bind(this.mapDblclick));

    this._mapInstance
      .on('dragstart')
      .pipe(takeUntil(this._destroy$))
      .subscribe(this.mapDragStart.emit.bind(this.mapDragStart));

    this._mapInstance
      .on('drag')
      .pipe(takeUntil(this._destroy$))
      .subscribe(this.mapDrag.emit.bind(this.mapDrag));

    this._mapInstance
      .on('dragend')
      .pipe(takeUntil(this._destroy$))
      .subscribe(this.mapDragEnd.emit.bind(this.mapDragEnd));

    this._mapInstance
      .on('centerChange')
      .pipe(takeUntil(this._destroy$))
      .subscribe(this.centerChange.emit.bind(this.centerChange));

    this._mapInstance
      .on('boundsChange')
      .pipe(takeUntil(this._destroy$))
      .subscribe(this.boundsChange.emit.bind(this.boundsChange));

    this._mapInstance
      .on('zoomChange')
      .pipe(takeUntil(this._destroy$))
      .subscribe(this.zoomChange.emit.bind(this.zoomChange));

    this._mapInstance
      .on('styleChange')
      .pipe(takeUntil(this._destroy$))
      .subscribe(this.styleChange.emit.bind(this.styleChange));
  }

  private _initDrawingManager(): void {
    this._drawingManager = new GoogleDrawingManager(this._mapInstance, {
      polygonOptions: {
        editable: false,
        fillColor: COLORS['accent-600'],
        fillOpacity: 0.25,
        strokeColor: COLORS['accent-600'],
        strokeOpacity: 1,
        strokeWeight: 2,
      },
      circleOptions: {
        editable: false,
        fillColor: COLORS['accent-600'],
        fillOpacity: 0.25,
        strokeColor: COLORS['accent-600'],
        strokeOpacity: 1,
        strokeWeight: 2,
      },
      rectangleOptions: {
        editable: false,
        fillColor: COLORS['accent-600'],
        fillOpacity: 0.25,
        strokeColor: COLORS['accent-600'],
        strokeOpacity: 1,
        strokeWeight: 2,
      },
    });

    this._drawingManager
      .on('polygoncomplete')
      .pipe(takeUntil(this._destroy$), takeUntil(this._mapInstance.on('styleChange')))
      .subscribe((polygon: google.maps.Polygon) => {
        this._createShape('polygon', this._drawingMode, polygon);
        this._drawingManager.setDrawingMode();
      });
    this._drawingManager
      .on('circlecomplete')
      .pipe(takeUntil(this._destroy$), takeUntil(this._mapInstance.on('styleChange')))
      .subscribe((circle: google.maps.Circle) => {
        this._createShape('circle', this._drawingMode, circle);
        this._drawingManager.setDrawingMode();
      });
    this._drawingManager
      .on('rectanglecomplete')
      .pipe(takeUntil(this._destroy$), takeUntil(this._mapInstance.on('styleChange')))
      .subscribe((rectangle: google.maps.Rectangle) => {
        this._createShape('rectangle', this._drawingMode, rectangle);
        this._drawingManager.setDrawingMode();
      });

    this._drawingManager
      .on('polylinecomplete')
      .pipe(takeUntil(this._destroy$), takeUntil(this._mapInstance.on('styleChange')))
      .subscribe((polyline: google.maps.Polyline) => {
        this._createShape('polyline', this._drawingMode, polyline);
        this._drawingManager.setDrawingMode();
      });
  }

  private _initPenMode(): void {
    this._mapInstance.mapInstance.setOptions({
      draggable: false,
      scrollwheel: false,
      disableDoubleClickZoom: true,
      draggableCursor: 'crosshair',
    });
    google.maps.event.addListener(this._mapInstance.mapInstance, 'mousedown', () => {
      this._penDrawingMode();
    });
  }

  private _penDrawingMode(): void {
    const freeDrawPolyline = new google.maps.Polyline({
      map: this._mapInstance.mapInstance,
      clickable: false,
      strokeColor: BASE_COLORS['accent-400'],
      strokeOpacity: 1,
      strokeWeight: 1,
    });

    const penMoveCallListener = google.maps.event.addListener(
      this._mapInstance.mapInstance,
      'mousemove',
      (e) => {
        freeDrawPolyline.getPath().push(e.latLng);
      },
    );

    google.maps.event.addListenerOnce(this._mapInstance.mapInstance, 'mouseup', () => {
      google.maps.event.removeListener(penMoveCallListener);
      const path = freeDrawPolyline.getPath();
      freeDrawPolyline.setMap(null);
      const polygon = new google.maps.Polygon({
        paths: [path],
        editable: true,
        draggable: true,
        strokeColor: BASE_COLORS['accent-400'],
        strokeOpacity: 1.0,
        strokeWeight: 6,
      });
      const dataLayer = new google.maps.Data();
      dataLayer.add(
        new google.maps.Data.Feature({
          geometry: new google.maps.Data.Polygon([
            polygon
              .getPath()
              .getArray()
              .map((latLng) => new google.maps.LatLng(latLng.lat(), latLng.lng())),
          ]),
        }),
      );
      dataLayer.toGeoJson((featureCollection: any) => {
        const tolerance = 0.005 / (this._mapInstance.getZoom() * 2);
        const simplifiedGeojson = turf.default(featureCollection, {
          tolerance,
          highQuality: true,
          mutate: true,
        });
        const paths = simplifiedGeojson.features[0].geometry.coordinates[0].map((position) => {
          return new google.maps.LatLng(position[1], position[0]);
        });
        const simplifiedPoly = new google.maps.Polygon({
          paths,
          editable: false,
          draggable: false,
          strokeColor: BASE_COLORS['accent-400'],
          strokeOpacity: 1.0,
          strokeWeight: 6,
        });
        google.maps.event.clearListeners(this._mapInstance.mapInstance, 'mousedown');
        this._mapInstance.newShape(simplifiedPoly);
        this._resetMapOptions();
      });
    });
  }

  private _resetMapOptions(): void {
    this._mapInstance.mapInstance.setOptions({
      draggable: true,
      scrollwheel: true,
      disableDoubleClickZoom: false,
      draggableCursor: '',
    });
  }

  private _createShape(
    tool: 'circle' | 'point' | 'polygon' | 'polyline' | 'rectangle' | 'pen',
    mode: FsMapControlsModes,
    shape: any,
  ): void {
    switch (tool) {
      case 'circle':
        const circle = new FsMapCircle(shape);
        this._mapInstance.newShape({
          shape: circle,
          mode,
        });
        break;
      case 'point':
        break;
      case 'polygon':
        const polygon = new FsMapPolygon(shape);
        this._mapInstance.newShape({
          shape: polygon,
          mode,
        });
        break;
      case 'rectangle':
        const rectangle = new FsMapRectangle(shape);
        this._mapInstance.newShape({
          shape: rectangle,
          mode,
        });
        break;
      case 'polyline':
      case 'pen':
        const polyline = new FsMapPolyline(shape);
        this._mapInstance.newShape({
          shape: polyline,
          mode,
        });
        break;
    }
  }
}
